import { Injectable } from "@angular/core";

import { LoginInfo } from "src/app/ui/models/domains/pbm/user/login-info";
import { UserCredentials } from "../models/domains/credentials/user-credentials.model";
import { AbstractParser } from "../repositories/parsers/parser";
import { environment } from "../../../environments/environment";
import { ServiceCredentials } from "../models/domains/credentials/service-credentials.model";
import { ReleaseNote } from "../models/domains/news/release-note.model";
import { ReleaseNoteEntity, ReleaseNoteItemEntity } from "../models/domains/news/release-note-entity.model";
import { ReleaseNoteItem } from "../models/domains/news/release-note-item.model";
import { UserOptions } from "../models/domains/options/user-options-model";
import { UnknownUser, User } from "../models/domains/pbm/user/user";
import { UserFactory } from "../models/domains/pbm/user/user.factory";
import { UserSearchPreferences } from "../models/domains/pbm/user/user-search-preferences";
import { UserSearchPreferencesFactory } from "../models/domains/pbm/user/user-search-preferences.factory";
import { UserTenant } from "../models/domains/pbm/user/user-tenant";

const USER_CREDENTIAL_KEY = `user-credential-${environment.appName}`;
const SERVICE_CREDENTIAL_KEY = "service-credential";
const RELEASE_NOTE_KEY = "releaseNote";
const USER_OPTION_KEY = `user-option-${environment.appName}`;
const USER_PROFILE_KEY = "user-profile";

const PAGE_VIEW_KEY = "active-page-view";
const FEATURE_VIEW_KEY = "active-feature-view";

const USER = "user";
const LOGIN_INFO = "login-info";
const CURRENT_TENANT = "tenant-identifier";

@Injectable()
export class AppContext {
  private userCredential: StorageEntry<UserCredentials>;
  private serviceCredential: StorageEntry<ServiceCredentials>;
  private userOption: StorageEntry<UserOptions>;
  private userSearchPreferences: StorageEntry<UserSearchPreferences>;
  private lastReleaseNoteStoreage: StorageEntry<ReleaseNoteEntity>;

  private pageView: StorageEntry<string>;
  private featureView: StorageEntry<string>;

  private user: StorageEntry<User>;
  private loginInfo: StorageEntry<LoginInfo>;
  private currentTenant: StorageEntry<UserTenant>;

  constructor() {
    this.userCredential = new LocalStorageEntry<UserCredentials>(USER_CREDENTIAL_KEY);
    this.userOption = new LocalStorageEntry<UserOptions>(USER_OPTION_KEY);
    this.userSearchPreferences = new LocalStorageEntry<UserSearchPreferences>(USER_PROFILE_KEY);
    this.serviceCredential = new LocalStorageEntry<ServiceCredentials>(SERVICE_CREDENTIAL_KEY);
    this.lastReleaseNoteStoreage = new LocalStorageEntry<ReleaseNoteEntity>(RELEASE_NOTE_KEY);

    this.pageView = new LocalStorageEntry<string>(PAGE_VIEW_KEY);
    this.featureView = new LocalStorageEntry<string>(FEATURE_VIEW_KEY);

    this.user = new LocalStorageEntry<User>(USER);
    this.loginInfo = new LocalStorageEntry<LoginInfo>(LOGIN_INFO);
    this.currentTenant = new LocalStorageEntry<UserTenant>(CURRENT_TENANT);
  }

  public setCurrentTenant(tenant: UserTenant): void {
  
    tenant.tenantStyle = {
      imagePath: tenant && tenant.tenantStyle && tenant.tenantStyle.imagePath ? tenant.tenantStyle.imagePath : "./assets/images/logo-healthy-completa.svg",
      buttonBackgroundColor: tenant && tenant.tenantStyle && tenant.tenantStyle.buttonBackgroundColor ? tenant.tenantStyle.buttonBackgroundColor : "#9389fb",
      fontColor: tenant && tenant.tenantStyle && tenant.tenantStyle.fontColor ? tenant.tenantStyle.fontColor : "#fefefe",
    };
     this.currentTenant.set(tenant);

  }
  public getCurrentTenant(): UserTenant {
    return (
      this.currentTenant.get() ||

    {
      id: "",
      key: "",
      description: "",
      useDataQuality: false,
      useBenefitDiscount: false,
      tenantStyle: {
      imagePath: "",
      buttonBackgroundColor: "#9389fb",
      fontColor: "#fefefe",
      },
      }

    );
  }

  public clearCurrentTenant(): void {
    this.currentTenant.remove();
  }
  public setUser(user: User): void {
    this.user.set(user);
  }

  public getUser(): User {
    const user = this.user.get();
    if (!!user) {
      return UserFactory.createUser(user);
    }
    return new UnknownUser();
  }

  public clearUser(): void {
    this.user.remove();
  }

  public setLoginInfo(loginInfo: LoginInfo): void {
    this.loginInfo.set(loginInfo);
  }

  public getLoginInfo(): LoginInfo {
    return this.loginInfo.get();
  }

  public clearLoginInfo(): void {
    this.loginInfo.remove();
  }

  public setActivePageView(page: string): void {
    this.pageView.set(page);
  }

  public getActivePageView() {
    return this.pageView.get();
  }

  public clearActivePageView(): void {
    this.pageView.remove();
  }

  public setActiveFeatureView(feature: string): void {
    this.featureView.set(feature);
  }

  public getActiveFeatureView() {
    return this.featureView.get();
  }

  public clearActiveFeatureView(): void {
    this.featureView.remove();
  }

  public getBackendUrl(): string {
    return environment.backendUrl;
  }

  public getTotalPerPage(): number {
    return environment.totalPerPage;
  }

  public getUserSearchPreferences(): UserSearchPreferences {
    const userSearchPreferences = this.userSearchPreferences.get();
    return UserSearchPreferencesFactory.create(userSearchPreferences);
  }

  public setUserSearchPreferences(value: UserSearchPreferences): void {
    this.userSearchPreferences.set(value);
  }

  public setLastReleaseNote(releaseNote: ReleaseNote): void {
    const entity = new ReleaseNoteEntity();
    entity.name = releaseNote.getName();
    entity.items = releaseNote.getItems().map((i) => {
      const item = new ReleaseNoteItemEntity();
      item.description = i.getDescription();
      item.releaseNoteType = i.getType();
      item.title = i.getTitle();
      return item;
    });
    this.lastReleaseNoteStoreage.set(entity);
  }

  public setUserOption(option: UserOptions) {
    this.userOption.set(option);
  }

  public getLastReleaseNote(): ReleaseNote | null {
    const entity = this.lastReleaseNoteStoreage.get();
    if (entity) {
      const items: Array<ReleaseNoteItem> = [];
      if (entity.items) {
        entity.items.forEach((item) => {
          items.push(new ReleaseNoteItem(item.title, item.description, item.releaseNoteType));
        });
      }
      return new ReleaseNote(entity.name, items);
    }
    return null;
  }

  public setUserCredential(credential: UserCredentials) {
    this.userCredential.set(credential);
  }

  public setServiceCredential(credential: ServiceCredentials) {
    this.serviceCredential.set(credential);
  }

  public getServiceCredential(): ServiceCredentials | null {
    return this.serviceCredential.get();
  }

  public getUserCredential(): UserCredentials | null {
    return this.userCredential.get();
  }

  public getUserOption(): UserOptions | null {
    const userOptionsStorage = this.userOption.get();
    const userOption = new UserOptions();
    userOption.setRemember(userOptionsStorage.remember);
    return userOption;
  }

  public clearUserCredential() {
    this.userCredential.remove();
  }

  public clearServiceCredential() {
    this.serviceCredential.remove();
  }
}

abstract class StorageEntry<T> {
  protected constructor(protected key: string, protected parser: AbstractParser<T> | any = null, private storage: Storage) {}

  protected parse(entity): T | null {
    return this.parser.parse(entity);
  }

  get(): T | null {
    const entity = this.storage.getItem(this.key);
    if (entity) {
      const entityJson = JSON.parse(entity);
      if (this.parser) {
        return Array.isArray(entityJson) ? this.parser.parseList(entityJson) : this.parser.parse(entityJson);
      }
      return entityJson;
    }
    return null;
  }

  set(item: T) {
    this.storage.setItem(this.key, JSON.stringify(item));
  }

  remove() {
    this.storage.removeItem(this.key);
  }
}

class LocalStorageEntry<T> extends StorageEntry<T> {
  constructor(protected key: string, protected parser: AbstractParser<T> | any = null) {
    super(key, parser, localStorage);
  }
}

class SessionStorageEntry<T> extends StorageEntry<T> {
  constructor(protected key: string, protected parser: AbstractParser<T> | any = null) {
    super(key, parser, sessionStorage);
  }
}
